/**
 * TypographyConcepts — founder-only preview page.
 *
 * Route: /demo/typography-concepts  (not linked from anywhere public)
 *
 * Compares the CURRENT font stack (Cormorant Garamond + Manrope) against
 * two alternate serif/sans pairings, using REAL site copy across the
 * whole type scale (H1 → H2 → body → caption → button) so we can judge
 * how each pair holds up as the eye scans a real page — not just a
 * hero moment.
 *
 * All three families ship free on Google Fonts. No proprietary licensing.
 */
import { useEffect } from "react";

import { Navbar } from "../components/Navbar";
import { useNoIndex } from "../lib/useNoIndex";

/* ─── Font imports (injected once on mount) ─────────────────────────
   We load the two ALTERNATE pairs on this preview page only, so the
   rest of the site isn't slowed down by fonts we're just evaluating.
   Fraunces is already loaded site-wide by index.css. */
const ALT_FONTS_HREF =
  "https://fonts.googleapis.com/css2?" +
  "family=Karla:ital,wght@0,300..800;1,300..800&" +
  "family=Alegreya:ital,wght@0,400..900;1,400..900&" +
  "family=Figtree:ital,wght@0,300..900;1,300..900&" +
  "display=swap";

const useAltFonts = () => {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${ALT_FONTS_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = ALT_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
};


/* ─── The three concepts ──────────────────────────────────────────── */

const CONCEPTS = [
  {
    key: "current",
    label: "Current",
    title: "Cormorant Garamond + Manrope.",
    vibe: "Editorial-classic + modern geometric sans",
    pros: [
      "Serif reads like a book jacket — warm and literary.",
      "Manrope is a workhorse — everyone can read it.",
    ],
    cons: [
      "Manrope is the ChatGPT-generated-startup default — you'll see it on a hundred landing pages this week.",
      "Cormorant's fine italic hair strokes can look precious at small sizes.",
    ],
    css: {
      // The current site fonts (already loaded globally)
      serif: "'Cormorant Garamond', serif",
      sans:  "'Manrope', sans-serif",
    },
  },
  {
    key: "editorial-weekly",
    label: "Pairing A",
    title: "Fraunces + Karla.",
    vibe: "Editorial magazine — Kinfolk / Cereal / Apartamento",
    pros: [
      "Fraunces has a 'SOFT' axis — literally engineered to warm serif type on-screen.",
      "Karla is a humanist grotesque with real character — the opposite of the Inter/Manrope defaults.",
      "Both are free on Google Fonts with generous weight ranges.",
    ],
    cons: [
      "Fraunces is trending in editorial 2024-2026 — recognizable to design-adjacent readers, but nowhere near overused.",
    ],
    css: {
      serif: "'Fraunces', 'Cormorant Garamond', serif",
      sans:  "'Karla', 'Manrope', sans-serif",
    },
    styleHacks: {
      // Fraunces variable-axis tuning — soft terminals, book optical size
      serifStyle: { fontVariationSettings: "'SOFT' 100, 'opsz' 144, 'wght' 500" },
    },
  },
  {
    key: "community-craft",
    label: "Pairing B",
    title: "Alegreya + Figtree.",
    vibe: "Community bookshop / craft nonprofit / small-batch publisher",
    pros: [
      "Alegreya was designed by an Argentinian type designer explicitly for humanist warmth — TDC award winner.",
      "Figtree is a modern geometric sans with rounded terminals — reads friendlier than Manrope at every size.",
      "Distinctive enough that neither will show up on a competing SaaS site.",
    ],
    cons: [
      "Alegreya's warmth pushes slightly bookish — need to pair with strong photography to stay contemporary.",
    ],
    css: {
      serif: "'Alegreya', 'Cormorant Garamond', serif",
      sans:  "'Figtree', 'Manrope', sans-serif",
    },
  },
];


/* ─── Mockup card — shows one concept at real type scale ────────── */

const TypeMockup = ({ concept }) => {
  const { css, styleHacks = {} } = concept;
  const serifStyle = { fontFamily: css.serif, ...(styleHacks.serifStyle || {}) };
  const sansStyle  = { fontFamily: css.sans };

  return (
    <div
      data-testid={`type-mockup-${concept.key}`}
      className="rounded-2xl border border-border bg-bone p-8 shadow-[0_18px_44px_-22px_rgba(26,23,20,0.28)] lg:p-10"
    >
      {/* Concept label chip */}
      <div className="mb-8 flex items-baseline gap-3">
        <span className="rounded-full bg-yellow px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-ink">
          {concept.label}
        </span>
        <h2 style={serifStyle} className="text-xl leading-tight text-ink">
          {concept.title}
        </h2>
      </div>
      <p style={sansStyle} className="mb-8 text-[13px] italic text-olive">
        {concept.vibe}
      </p>

      {/* ── The type-scale mockup ── */}
      {/* Eyebrow */}
      <p
        style={sansStyle}
        className="text-[10px] font-semibold uppercase tracking-[0.24em] text-cobalt"
      >
        Our promise
      </p>

      {/* H1 */}
      <h3
        style={serifStyle}
        className="mt-4 text-4xl leading-[0.98] tracking-tight text-ink lg:text-5xl"
      >
        The costs aid <em style={serifStyle} className="italic text-tangerine">forgets</em>. The people who don't.
      </h3>

      {/* Deck / subtitle */}
      <p
        style={sansStyle}
        className="mt-5 max-w-md text-base leading-relaxed text-olive"
      >
        Aunties, uncles, mentors — the quiet second family that shows up
        with grocery money, a laptop, a flight home. My Rich Aunt turns
        that instinct into a real, tax-deductible way to help.
      </p>

      {/* H2 — section heading */}
      <h4
        style={serifStyle}
        className="mt-10 text-2xl leading-tight text-ink"
      >
        Small gifts. Real students. Verified impact.
      </h4>

      {/* Body copy — a normal paragraph */}
      <p style={sansStyle} className="mt-4 text-[15px] leading-relaxed text-ink/85">
        Every student on the platform is confirmed as enrolled before
        their campaign goes live. Every grant is awarded by My Rich Aunt
        to a verified student for a specific need — books, a flight home,
        move-in essentials — and donors see exactly what their support
        made possible. No overhead skim, no gimmicks.
      </p>

      {/* Caption */}
      <p style={sansStyle} className="mt-4 text-[12px] italic text-olive/80">
        A 501(c)(3) public charity. Every gift is tax-deductible.
      </p>

      {/* CTA row */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          type="button"
          style={sansStyle}
          className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand"
        >
          Become a founding donor
        </button>
        <button
          type="button"
          style={sansStyle}
          className="rounded-full border border-ink/20 bg-bone px-5 py-2.5 text-sm font-semibold text-ink"
        >
          Browse campaigns
        </button>
      </div>

      {/* Pros / Cons — actual designer notes so the founder sees the trade-offs */}
      <div className="mt-10 grid grid-cols-1 gap-4 border-t border-ink/10 pt-6 lg:grid-cols-2">
        <div>
          <p style={sansStyle} className="text-[10px] font-semibold uppercase tracking-[0.22em] text-grass">
            Why we like it
          </p>
          <ul style={sansStyle} className="mt-2 space-y-2 text-[13px] text-ink/85">
            {concept.pros.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-grass" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p style={sansStyle} className="text-[10px] font-semibold uppercase tracking-[0.22em] text-scarlet">
            Trade-offs
          </p>
          <ul style={sansStyle} className="mt-2 space-y-2 text-[13px] text-ink/85">
            {concept.cons.map((c, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-scarlet" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Fonts fingerprint */}
      <p
        style={sansStyle}
        className="mt-8 text-[11px] leading-relaxed text-olive/80"
      >
        <span className="font-semibold">Serif —</span>{" "}
        <span style={serifStyle}>{css.serif.split(",")[0].replace(/'/g, "")}</span>{" "}
        &nbsp;·&nbsp;{" "}
        <span className="font-semibold">Sans —</span>{" "}
        <span>{css.sans.split(",")[0].replace(/'/g, "")}</span>
      </p>
    </div>
  );
};


/* ─── Page ────────────────────────────────────────────────────────── */

export default function TypographyConcepts() {
  useNoIndex();
  useAltFonts();

  return (
    <main
      data-testid="typography-concepts-page"
      className="relative min-h-screen bg-sand text-ink"
    >
      <Navbar
        onStart={() => {}}
        primaryLabel="Preview the platform"
        secondaryLabel={null}
      />

      <section className="mx-auto max-w-6xl px-6 pb-16 pt-32 lg:px-8 lg:pt-44">
        <span
          className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt"
        >
          Typography concepts
        </span>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
          Two alternate pairs, tested against{" "}
          <em className="italic text-tangerine">real site copy</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
          Each mockup below renders the exact type scale the live site uses —
          eyebrow, H1, deck, H2, body, caption, buttons — in a candidate serif +
          sans pair. Read the H1 aloud in each, then scan the body copy. Whichever
          feels more like a magazine you'd pick up (and less like a template) is
          the winner. Pick one and I'll swap the whole site in a single pass.
        </p>
        <p className="mt-3 max-w-2xl text-[13px] italic text-olive">
          Preview only · not linked from anywhere public.
        </p>
      </section>

      <section className="mx-auto max-w-6xl space-y-16 px-6 pb-24 lg:px-8 lg:space-y-20">
        {CONCEPTS.map((c) => (
          <TypeMockup key={c.key} concept={c} />
        ))}
      </section>
    </main>
  );
}
